import { formatIDR } from "@marketplace/shared/utils";
import { Accordion, Skeleton, Text } from "@marketplace/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type ClaimEstimationBreakdownProps = {
  invoiceAmount: number;
  estimation?: number;
  payoutFee?: number;
  isEstimationLoading?: boolean;
  keyPrefix?: "claimOrderPage.submitModal" | "claimHistoryPage.reviewModal";
  totalProductPrice?: number;
  totalPpn?: number;
  totalPph23?: number;
};

export const ClaimEstimationBreakdown = ({
  invoiceAmount,
  estimation,
  payoutFee,
  isEstimationLoading,
  keyPrefix = "claimOrderPage.submitModal",
  totalProductPrice,
  totalPpn,
  totalPph23
}: ClaimEstimationBreakdownProps) => {
  const { t } = useTranslation("order", {
    keyPrefix
  });

  const [accordionValue, setAccordionValue] = useState<string | null>(null);
  const isOpen = accordionValue === "breakdown";

  const productPrice = totalProductPrice ?? 0;
  const ppn = totalPpn ?? 0;
  const pph23 = totalPph23 ?? 0;

  if (!invoiceAmount) {
    return null;
  }

  return (
    <div className="rounded-lg bg-mischka-100 border border-solid border-mischka-400 p-3 space-y-3">
      <Accordion
        value={accordionValue}
        onChange={setAccordionValue}
        classNames={{
          control: "px-0 py-0",
          content: "px-0",
          item: "border-0"
        }}
      >
        <Accordion.Item value="breakdown">
          <Accordion.Control>
            <Text type="text" className="text-fjord-grey-500 font-semibold">
              {t("viewBreakdown")}
            </Text>
          </Accordion.Control>
          <Accordion.Panel>
            <div className="space-y-4 mt-3">
              <Row label={t("productPrice")} value={formatIDR(productPrice)} />
              <Row label={t("ppn")} value={formatIDR(ppn)} />
              <Row
                label={t("pph23")}
                value={`- ${formatIDR(pph23)}`}
                valueClassName="text-[#FF3B30]"
              />
              <Row
                label={t("payoutFee")}
                value={`- ${formatIDR(payoutFee || 0)}`}
                valueClassName="text-[#FF3B30]"
              />

              <hr className="my-3 bg-mischka-400 h-px border-0 w-full" />
              <div className="flex justify-between items-center pt-1">
                <div className="flex flex-col gap-1">
                  <Text
                    type="text"
                    className="text-fjord-grey-500 font-semibold"
                  >
                    {t("estimatedAmount")}
                  </Text>
                  <Text type="text" size="s" className="text-fjord-grey-500">
                    {t("estimationFormula")}
                  </Text>
                </div>
                <Text
                  type="text"
                  size="l"
                  className="text-noble-blue-500 font-semibold"
                >
                  {formatIDR(estimation ?? 0)}
                </Text>
              </div>
            </div>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      {!isOpen && (
        <>
          {isEstimationLoading ? (
            <Skeleton height={24} width={250} />
          ) : estimation != null ? (
            <div className="flex justify-between items-center pt-1">
              <div className="flex flex-col gap-1">
                <Text type="text" className="text-fjord-grey-500 font-semibold">
                  {t("estimatedAmount")}
                </Text>
                <Text type="text" size="s" className="text-fjord-grey-500">
                  {t("estimationFormula")}
                </Text>
              </div>
              <Text
                type="text"
                size="l"
                className="text-noble-blue-500 font-semibold"
              >
                {formatIDR(estimation)}
              </Text>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

type RowProps = {
  label: string;
  value: string;
  labelClassName?: string;
  valueClassName?: string;
};

const Row = ({ label, value, labelClassName, valueClassName }: RowProps) => (
  <div className="flex justify-between items-center">
    <Text
      type="text"
      size="s"
      className={`text-fjord-grey-500 ${labelClassName ?? ""}`}
    >
      {label}
    </Text>

    <Text
      type="text"
      size="s"
      className={`${valueClassName ?? "text-fjord-grey-500"}`}
    >
      {value}
    </Text>
  </div>
);
